import { useState, useEffect } from "react";
import { ProfileSettings } from "../shared/ProfileSettings";
import { Leaderboard } from "../shared/Leaderboard";
import { ChildRewardsPage } from "./ChildRewardsPage";
import { getUserLayoutPreference, setUserLayoutPreference } from "../../utils/firebase";
import { GoalsTab } from "./GoalsTab";
import { AlertsTab } from "./AlertsTab";
import { ModernHomeLayout } from "../../layouts/ModernHomeLayout";
import { AdventureHomeLayout } from "../../layouts/AdventureHomeLayout";
import { StorybookHomeLayout } from "../../layouts/StorybookHomeLayout";
import { SpaceHomeLayout } from "../../layouts/SpaceHomeLayout";
import { WizardHomeLayout } from "../../layouts/WizardHomeLayout";
import { AviationHomeLayout } from "../../layouts/AviationHomeLayout";

// Modern and Adventure are hidden from the switcher for now but still render
// if a user has them saved as their preference (so no data loss on upgrade).
const LAYOUTS = [
  { id: "storybook", label: "📓 Journal" },
  { id: "space",     label: "🚀 Space" },
  { id: "wizard",    label: "🧙 Wizard" },
  { id: "aviation",  label: "✈️ Pilot" },
];

// CSS variable overrides applied to the ChildHome wrapper so ALL tabs
// (Goals, Alerts, Rewards, Leaderboard, tab bar) automatically inherit
// each layout's colour palette via the normal cascade — no changes needed
// in the individual tab components.
const LAYOUT_THEMES = {
  storybook: {
    bg: "#fefcf3",
    "--bg": "#fefcf3",
    "--surface": "#ffffff",
    "--surface2": "#fef9f0",
    "--border": "#fde68a",
    "--ink": "#1f2937",
    "--navy": "#92400e",
    "--muted": "#9ca3af",
    "--blue": "#f472b6",
    "--blue-light": "#fce7f3",
    "--green": "#16a34a",
    "--green-light": "#dcfce7",
    "--amber": "#d97706",
    "--amber-light": "#fef3c7",
    "--radius": "12px",
    "--shadow": "0 2px 8px rgba(0,0,0,0.06)",
    "--shadow-lg": "0 8px 24px rgba(0,0,0,0.1)",
    "--heading-font": "'Caveat', cursive",
  },
  space: {
    bg: "#020617",
    tabLabels: { home: "🏠 HOME", rewards: "💎 SHOP", board: "🏆 RANK", goals: "🎯 GOALS", alerts: "📡 COMMS" },
    "--bg": "#020617",
    "--surface": "#0f172a",
    "--surface2": "#1e293b",
    "--border": "rgba(34,211,238,0.2)",
    "--ink": "#f1f5f9",
    "--navy": "#22d3ee",
    "--muted": "#64748b",
    "--blue": "#22d3ee",
    "--blue-light": "rgba(34,211,238,0.15)",
    "--green": "#10b981",
    "--green-light": "rgba(16,185,129,0.15)",
    "--amber": "#fbbf24",
    "--amber-light": "rgba(251,191,36,0.15)",
    "--radius": "14px",
    "--shadow": "0 2px 12px rgba(0,0,0,0.4)",
    "--shadow-lg": "0 8px 32px rgba(0,0,0,0.6)",
    "--heading-font": "'Orbitron', sans-serif",
  },
  wizard: {
    bg: "#1c1917",
    tabLabels: { home: "🏰 Home", rewards: "✨ Shop", board: "⚔️ Duel", goals: "🎯 Quests", alerts: "🦉 Owls" },
    "--bg": "#1c1917",
    "--surface": "#292524",
    "--surface2": "#3b2f1e",
    "--border": "#78350f",
    "--ink": "#fde68a",
    "--navy": "#fbbf24",
    "--muted": "#a8a29e",
    "--blue": "#7c3aed",
    "--blue-light": "rgba(124,58,237,0.2)",
    "--green": "#16a34a",
    "--green-light": "rgba(22,163,74,0.2)",
    "--amber": "#f59e0b",
    "--amber-light": "rgba(245,158,11,0.2)",
    "--radius": "8px",
    "--shadow": "0 2px 12px rgba(0,0,0,0.5)",
    "--shadow-lg": "0 8px 32px rgba(0,0,0,0.7)",
    "--heading-font": "'Cinzel Decorative', serif",
  },
  aviation: {
    bg: "#0a1628",
    tabLabels: { home: "✈️ HOME", rewards: "🎁 SHOP", board: "🏆 RANK", goals: "🎯 GOALS", alerts: "📡 RADIO" },
    "--bg": "#0a1628",
    "--surface": "#0f1d32",
    "--surface2": "rgba(34,211,238,0.07)",
    "--border": "rgba(34,211,238,0.2)",
    "--ink": "#e2e8f0",
    "--navy": "#22d3ee",
    "--muted": "#64748b",
    "--blue": "#22d3ee",
    "--blue-light": "rgba(34,211,238,0.15)",
    "--green": "#4ade80",
    "--green-light": "rgba(74,222,128,0.15)",
    "--amber": "#f59e0b",
    "--amber-light": "rgba(245,158,11,0.15)",
    "--radius": "8px",
    "--shadow": "0 2px 12px rgba(0,0,0,0.5)",
    "--shadow-lg": "0 8px 32px rgba(0,0,0,0.7)",
    "--heading-font": "'Exo 2', sans-serif",
  },
};

export function ChildHome({ user, data, onStartTest, onStartMeaningTest, onStartSynonymTest, onStartAntonymTest, onDataChange, settings }) {
  const [showProfile, setShowProfile] = useState(false);
  const [tab, setTab] = useState("home");
  const [layoutPreference, setLayoutPreference] = useState("storybook");

  // Destructure bg + tabLabels out; the rest are CSS vars spread onto the wrapper div
  const { bg: themeBg, tabLabels: themeTabLabels, ...themeCssVars } = LAYOUT_THEMES[layoutPreference] || {};

  const mySessions       = data.sessions.filter(s => s.userId === user.id);
  const today            = new Date().toDateString();
  const todayPts         = mySessions.filter(s => new Date(s.date).toDateString() === today).reduce((a, s) => a + (s.ptsEarned || 0), 0);
  const pendingCount     = (data.requests || []).filter(r => r.userId === user.id && r.status === "pending").length;

  useEffect(() => {
    getUserLayoutPreference(user.id).then(pref => setLayoutPreference(pref));
  }, [user.id]);

  function switchLayout(layout) {
    setLayoutPreference(layout);
    setUserLayoutPreference(user.id, layout);
  }

  if (showProfile) return <ProfileSettings user={user} data={data} onDataChange={onDataChange} onClose={() => setShowProfile(false)} />;

  const layoutProps = {
    user, data,
    onStartTest, onStartMeaningTest, onStartSynonymTest, onStartAntonymTest,
    onDataChange, settings,
    onShowProfile: () => setShowProfile(true),
  };

  function renderLayout() {
    switch (layoutPreference) {
      case "adventure":  return <AdventureHomeLayout  {...layoutProps} />;
      case "storybook":  return <StorybookHomeLayout  {...layoutProps} />;
      case "space":      return <SpaceHomeLayout       {...layoutProps} />;
      case "wizard":     return <WizardHomeLayout      {...layoutProps} />;
      case "aviation":   return <AviationHomeLayout    {...layoutProps} />;
      default:           return <ModernHomeLayout      {...layoutProps} />;
    }
  }

  return (
    <div style={{ paddingBottom: "4.5rem", minHeight: "100vh", background: themeBg, ...themeCssVars }}>

      {/* Layout switcher — shown only on home tab */}
      {tab === "home" && (
        <div style={{ display: "flex", gap: ".35rem", overflowX: "auto", padding: "0.75rem 1rem 0.5rem" }}>
          {LAYOUTS.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => switchLayout(id)}
              style={{
                flexShrink: 0,
                padding: ".35rem .85rem", borderRadius: 999, border: "none",
                fontWeight: 700, fontSize: ".78rem", cursor: "pointer",
                fontFamily: "'Nunito', sans-serif", transition: "all .15s",
                background: layoutPreference === id ? "var(--blue)" : "var(--surface2)",
                color: layoutPreference === id ? "#fff" : "var(--muted)",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}
      {tab !== "home" && (
        <div style={{ padding: "0.75rem 1rem 0" }}>
          {tab === "rewards"     && <ChildRewardsPage user={user} data={data} onDataChange={onDataChange} pointsEarned={todayPts} settings={settings} />}
          {tab === "leaderboard" && <Leaderboard currentUser={user} data={data} users={data.users} />}
          {tab === "goals"       && <GoalsTab user={user} data={data} onDataChange={onDataChange} settings={settings} />}
          {tab === "alerts"      && <AlertsTab user={user} data={data} />}
        </div>
      )}
      {tab === "home" && renderLayout()}

      {/* Bottom tab bar — fixed to viewport bottom */}
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0,
        background: "var(--surface)",
        borderTop: "1px solid var(--border)",
        zIndex: 99,
        padding: ".55rem 1.5rem .65rem",
        display: "flex",
        justifyContent: "center",
      }}>
        <div style={{ display: "flex", gap: ".45rem", maxWidth: 880, width: "100%", flexWrap: "wrap" }}>
          <button className={`tab-btn ${tab === "home" ? "on" : ""}`} onClick={() => setTab("home")}>{themeTabLabels?.home || "🏠 Home"}</button>
          <button className={`tab-btn ${tab === "rewards" ? "on" : ""}`} onClick={() => setTab("rewards")}>
            {themeTabLabels?.rewards || "🏆 Rewards"} {pendingCount > 0 && <span style={{ background: "var(--green)", color: "#fff", fontSize: ".65rem", fontWeight: 700, padding: ".1rem .4rem", borderRadius: 999, marginLeft: ".3rem" }}>{pendingCount}</span>}
          </button>
          <button className={`tab-btn ${tab === "leaderboard" ? "on" : ""}`} onClick={() => setTab("leaderboard")}>{themeTabLabels?.board || "🏅 Board"}</button>
          <button className={`tab-btn ${tab === "goals" ? "on" : ""}`} onClick={() => setTab("goals")}>{themeTabLabels?.goals || "🎯 Goals"}</button>
          <button className={`tab-btn ${tab === "alerts" ? "on" : ""}`} onClick={() => setTab("alerts")}>{themeTabLabels?.alerts || "🔔 Alerts"}</button>
        </div>
      </div>
    </div>
  );
}
